import React, {useState, useRef, useEffect} from 'react';
import ReactDOM from 'react-dom';
import {StyleSheet, View} from 'react-native';
import {
  delayUnmountingHelperHook,
  onEndDelayUnmounting,
  onTabPress,
  onKeyDown,
  getFocusableNodes,
} from './HelperFunctions';

const createModalContext = () => {
  // subscribers with callbacks for external updates
  let subscribers = [];
  let counter = 0;
  let hiddenElements = [];

  const allModalsAreClosed = () => {
    hiddenElements.forEach((element) => {
      element.setAttribute('aria-hidden', 'false');
    });
    hiddenElements = [];
  };
  const firstModalIsOpened = () => {
    // hide all root elements on page for screen readers (except dialogs since these are handled internally and already
    // hidden elements)
    hiddenElements = document.querySelectorAll(
      'body > div:not([aria-role=dialog]):not([aria-hidden=true])',
    );
    hiddenElements.forEach((element) => {
      element.setAttribute('aria-hidden', 'true');
    });
  };

  const show = (setCurrentModal) => {
    counter++;

    if (subscribers.length === 0) {
      firstModalIsOpened();
    }

    // onFocus all other modals
    subscribers.forEach((s) => s.setCurrentModal(false));

    // focus current modal
    setCurrentModal(true);

    // add to subscribers so we can focus or blur them based on other modals on screen
    subscribers.push({modalIndex: counter, setCurrentModal});
    return counter;
  };

  const remove = (idx) => {
    subscribers = subscribers.filter((s) => s.modalIndex !== idx);

    if (subscribers.length > 0) {
      // focus modal below the closed one
      const subscriberToFocus = subscribers[subscribers.length - 1];
      subscriberToFocus.setCurrentModal(true);
    } else {
      allModalsAreClosed();
    }
  };

  const use = (visible) => {
    const [currentModal, setCurrentModal] = useState(visible);
    useEffect(() => {
      if (visible) {
        const modalIndex = show(setCurrentModal);
        return () => remove(modalIndex);
      }
    }, [visible]);
    return currentModal;
  };

  return {
    use,
  };
};

const modalContext = createModalContext();
const animationDurationMs = 300;

const styles = StyleSheet.create({
  dialog: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    top: 0,
  },
  animated: {
    height: '100%',
    transitionDuration: `${animationDurationMs}ms`,
    transitionTimingFunction: 'linear',
    overflow: 'hidden',
  },
  none: {},
  fadeIn: {
    animationKeyframes: [{'0%': {opacity: 0}, '100%': {opacity: 1}}],
    animationDelay: 0,
    animationDuration: `${animationDurationMs}ms`,
    animationTimingFunction: 'linear',
  },
  slideIn: {
    animationKeyframes: [
      {
        '0%': {
          transform: [
            {
              translateY: '100%',
            },
          ],
        },
        '100%': {
          transform: [
            {
              translateY: 0,
            },
          ],
        },
      },
    ],
    animationDelay: 0,
    animationDuration: `${animationDurationMs}ms`,
    animationTimingFunction: 'linear',
  },
});

const startAnimation = {
  fade: styles.fadeIn,
  slide: styles.slideIn,
};

const getAnimationStyles = (visible) => ({
  none: {},
  slide: {
    transform: [
      {
        translateY: visible ? 0 : '100%',
      },
    ],
  },
  fade: {
    opacity: visible ? 1 : 0,
  },
});

export default function Modal({
  animationType = 'none',
  children,
  onRequestClose,
  visible,
}) {
  const [internalVisible, setInternalVisible] = useState(visible);
  const shouldDelayingUnmounting = !visible && animationType !== 'none';
  const animatedRef = useRef(null);
  const modalRef = useRef(null);
  const [delayUnmounting, setDelayUnmounting] = useState(false);
  const open = visible || delayUnmounting || internalVisible;
  const isCurrentModal = modalContext.use(open);

  delayUnmountingHelperHook(
    internalVisible,
    setInternalVisible,
    visible,
    setDelayUnmounting,
    shouldDelayingUnmounting,
  );

  onEndDelayUnmounting(open, animatedRef, setDelayUnmounting);

  getFocusableNodes(modalRef);

  onTabPress(getFocusableNodes, modalRef);

  onKeyDown(onRequestClose, onTabPress);

  useEffect(() => {
    if (visible && isCurrentModal) {
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
    }
  }, [isCurrentModal, visible]);

  const animationStyles = getAnimationStyles(visible);

  const animationStyle = animationStyles[animationType] || animationStyles.none;

  return open
    ? ReactDOM.createPortal(
        <View
          ref={modalRef}
          aria-role="dialog"
          aria-hidden={!open || !isCurrentModal}
          aria-modal={true}
          style={[styles.dialog, {zIndex: 1000}]}>
          <View
            ref={animatedRef}
            style={[
              styles.animated,
              animationStyle,
              startAnimation[animationType] || null,
            ]}>
            {open ? children : null}
          </View>
        </View>,
        document.body,
      )
    : null;
}
